"use client";

import React, { InputHTMLAttributes, useState, forwardRef } from "react";
import { cn } from "@/utils/cn";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  value?: string; // Este é o 'value' que RHF passará via Controller -> field
  error?: boolean;
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>((
  {
    id,
    label,
    type = "text",
    required = false,
    value: propValue, // Recebe o valor de RHF aqui
    error = false,
    className,
    ...props // Inclui onChange, onBlur, name de RHF
  },
  ref
) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  // Usar propValue diretamente para determinar se o label deve flutuar
  const shouldFloat = isFocused || (propValue && String(propValue).length > 0);

  const baseInputClasses = "peer w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors duration-200 ease-in-out";
  const normalInputClasses = "border-gray-300 focus:border-primary focus:ring-primary";
  const errorInputClasses = "border-red-500 focus:border-red-500 focus:ring-red-500";

  const baseLabelClasses = "absolute left-3 bg-white px-1 text-sm transition-all duration-200 ease-in-out pointer-events-none";
  const normalLabelClasses = "text-gray-400 peer-focus:text-primary";
  const errorLabelClasses = "text-red-600 peer-focus:text-red-600";

  const floatingLabelTransform = "-top-3 text-xs";
  const restingLabelTransform = "top-2.5 text-base";

  return (
    <div className={cn("relative w-full", className)}>
      <input
        ref={ref}
        type={type}
        id={id}
        onFocus={handleFocus}
        onBlur={handleBlur}
        required={required}
        className={cn(
          baseInputClasses,
          error ? errorInputClasses : normalInputClasses
        )}
        value={propValue || ''} // Definir o valor explicitamente aqui
        {...props} // Passa onChange, name, onBlur etc.
      />
      <label
        htmlFor={id}
        className={cn(
          baseLabelClasses,
          error ? errorLabelClasses : normalLabelClasses,
          shouldFloat ? floatingLabelTransform : restingLabelTransform
        )}
      >
        {label}
      </label>
    </div>
  );
});

Input.displayName = "Input";
